// frontend/src/components/auth/Auth.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithGoogle, 
  auth,
  setupPersistence
} from '../../firebase/config';
import { ensureUserResourceQuota, createSubscription } from '../subscription/subscriptionUtils';
import EmailPasswordAuth from './EmailPasswordAuth';
import EmailLinkAuth from './EmailLinkAuth';
import PasswordReset from './PasswordReset';

const Auth = ({ onClose, appId = 'saleswit', selectedPlan = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [authMethod, setAuthMethod] = useState('email-password'); 
  // Determine initial view based on URL or selectedPlan
  const [currentView, setCurrentView] = useState(() => {
    const path = window.location.pathname;
    if (path.includes('auth_login') || path.includes('login')) return 'login';
    if (path.includes('auth_signup') || path.includes('signup')) return 'signup';
    return 'signup'; // Default to signup
  });
  
  const navigate = useNavigate();

  // Add debug logging
  useEffect(() => {
  }, [selectedPlan]);

  // Set Firebase persistence to LOCAL (persist even when browser is closed)
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await setupPersistence();
      } catch (err) {
        console.error('Error setting persistence in Auth component:', err);
      }
    };

    initializeAuth();
  }, []);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    
    try {
      // First ensure persistence is set to LOCAL
      await setupPersistence();
      
      const result = await signInWithGoogle();
      const user = result.user;
      
      await ensureUserResourceQuota(user.uid, appId);
      
      // Check if this is a paid plan
      const isPaidPlan = selectedPlan && 
                        selectedPlan.id && 
                        selectedPlan.id !== 'free_plan' && 
                        selectedPlan.id !== 'login' && 
                        selectedPlan.id !== 'signup';
      
      if (isPaidPlan) {
        // Show payment loading and initiate payment for paid plans
        setLoading(false);
        setPaymentLoading(true);
        try {
          await createSubscription(user.uid, selectedPlan.id, appId);
          // Don't navigate here - let the payment flow handle navigation
        } catch (err) {
          console.error("Error creating subscription:", err);
          setPaymentLoading(false);
          window.location.href = '/subscription-dashboard';
        }
      } else {
        // Free plan or header auth - go directly to generate page
        if (typeof onClose === 'function') {
          onClose();
        }
        
        // Use window.location for more reliable navigation
        window.location.href = '/subscription-dashboard';
      }
    } catch (err) {
      console.error('Google login error:', err);
      setError('Failed to log in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    // Check if this is a paid plan
    const isPaidPlan = selectedPlan && 
                      selectedPlan.id && 
                      selectedPlan.id !== 'free_plan' && 
                      selectedPlan.id !== 'login' && 
                      selectedPlan.id !== 'signup';
    
    if (isPaidPlan) {
      setPaymentLoading(true);
      // Payment flow will be handled by subscription utils
      setTimeout(() => {
        window.location.href = '/subscription-dashboard';
      }, 2000);
    } else {
      if (typeof onClose === 'function') {
        onClose();
      }
      
      // Navigate to the main app
      setTimeout(() => {
        window.location.href = '/subscription-dashboard';
      }, 1000);
    }
  };

  // Show payment loading screen
  if (paymentLoading) {
    return (
      <div className="bg-white rounded-xl p-6 text-center max-w-sm mx-auto">
        <div className="relative w-12 h-12 mx-auto mb-4">
          <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-500 animate-spin"></div>
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Initializing Payment</h3>
        <p className="text-sm text-gray-600">Please wait...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-sm mx-auto overflow-hidden">
      {/* Compact Header - Removed subtitle */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 text-center">
        <h1 className="text-xl font-bold text-white">
          {currentView === 'signup' ? 'Join SalesWit' : 'Welcome Back'}
        </h1>
      </div>

      {/* Compact Form Content */}
      <div className="px-6 py-5 space-y-4">
        {/* More Prominent Welcome Message for Signup */}
        {currentView === 'signup' && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 rounded-lg p-4 mb-4 shadow-sm">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Important Information</h3>
              <div className="text-sm space-y-2">
                <div className="flex items-center bg-green-100 px-2 py-1 rounded">
                  <span className="text-green-500 mr-2 font-semibold">✓</span>
                  <span className="text-green-800 font-medium">Upgrade anytime after signup</span>
                </div>
                <div className="flex items-center bg-orange-100 px-2 py-1 rounded">
                  <span className="text-orange-500 mr-2 font-semibold">!</span>
                  <span className="text-orange-800 font-medium">Use 'Login' (not sign-up) if you are already on MarketFit </span>
                </div>
                <div className="flex items-center bg-blue-100 px-2 py-1 rounded">
                  <span className="text-blue-600 mr-2 font-semibold">★</span>
                  <span className="text-blue-800 font-medium">Google account users : only use Google sign-up</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Compact Toggle */}
        <div className="text-center pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            {currentView === 'signup' ? 'Have an account? ' : "Don't have an account? "}
            <button
              onClick={() => setCurrentView(currentView === 'signup' ? 'login' : 'signup')}
              className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              {currentView === 'signup' ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
        
        {/* Compact Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center mr-2">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-xs text-red-800">{error}</p>
          </div>
        )}

        {/* Compact Plan Message */}
        {selectedPlan && selectedPlan.id === 'free_plan' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-2">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-xs text-green-800">
              <span className="font-bold">Free Plan</span> - 3 reports per month!
            </p>
          </div>
        )}

        {showPasswordReset ? (
          <PasswordReset 
            onCancel={() => setShowPasswordReset(false)}
            onSuccess={() => {
              setShowPasswordReset(false);
              setCurrentView('login');
            }}
          />
        ) : (
          <div className="space-y-4">
            {/* Compact Google Button */}
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-50 border border-gray-300 hover:border-blue-300 rounded-lg py-3 px-4 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
            >
              <div className="flex items-center justify-center">
                {loading ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    <span className="text-sm font-medium text-gray-700">Signing in...</span>
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span className="text-sm font-medium text-gray-800">
                      {currentView === 'signup' ? 'Continue with Google' : 'Sign in with Google'}
                    </span>
                  </>
                )}
              </div>
            </button>

            {/* Compact Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-white text-gray-500">Or with email</span>
              </div>
            </div>
            
            {/* Auth Form */}
            <div>
              {authMethod === 'email-password' ? (
                <EmailPasswordAuth 
                  onClose={onClose} 
                  appId={appId} 
                  selectedPlan={selectedPlan}
                  mode={currentView}
                  onSuccess={handleAuthSuccess}
                />
              ) : (
                <EmailLinkAuth 
                  onClose={onClose} 
                  appId={appId} 
                  selectedPlan={selectedPlan}
                  mode={currentView}
                  onSuccess={handleAuthSuccess}
                />
              )}
            </div>
            
            {/* Compact Forgot Password */}
            {currentView === 'login' && authMethod === 'email-password' && (
              <div className="text-center">
                <button
                  onClick={() => setShowPasswordReset(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </div>
        )}

        {/* Compact Terms */}
        {currentView === 'signup' && (
          <div className="text-center">
            <p className="text-xs text-gray-500">
              By signing up, you agree to our{' '}
              <a href="/terms" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>{' '}
              and{' '}
              <a href="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;